import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  BarChart as ChartIcon,
  Assignment as ReportIcon
} from '@mui/icons-material';
import { 
  getProjectScreenplayAnalyses, 
  deleteScreenplayAnalysis,
  retryAnalysis
} from '../services/screenplayAnalysisService';
import ScreenplayAnalysisViewer from './ScreenplayAnalysisViewer';
import { usePermissions } from '../contexts/PermissionsContext';

/**
 * ScreenplayAnalysisManager Component
 * 
 * Manages screenplay analyses for a project, including creation, viewing, and deletion.
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - ID of the current project
 * @param {Object} props.selectedScript - The currently selected script
 */
const ScreenplayAnalysisManager = ({ projectId, projectName, selectedScript }) => {
  const theme = useTheme();
  const { can } = usePermissions();
  const canView = can('scripts.view');
  const canAnalyze = can('scripts.analyze');
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const refreshIntervalRef = useRef(null);
  const fetchAnalysesRef = useRef(null);

  const stopRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Fetch analyses; manages its own polling lifecycle based on fresh data
  const fetchAnalyses = useCallback(async () => {
    if (!projectId || !canView) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getProjectScreenplayAnalyses(projectId);

      const hasActiveAnalyses = data.some(
        a => a.status === 'pending' || a.status === 'processing'
      );

      if (hasActiveAnalyses) {
        // Ensure a single refresh interval is running
        if (!refreshIntervalRef.current) {
          refreshIntervalRef.current = setInterval(() => {
            fetchAnalysesRef.current?.();
          }, 5000);
        }
      } else {
        stopRefresh();
      }

      setAnalyses(data);
    } catch (err) {
      console.error('Error fetching analyses:', err);
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  }, [projectId, canView, stopRefresh]);

  // Keep a ref to the latest fetchAnalyses for the polling interval
  useEffect(() => {
    fetchAnalysesRef.current = fetchAnalyses;
  }, [fetchAnalyses]);

  // Fetch analyses when projectId changes; clean up polling on unmount
  useEffect(() => {
    if (projectId) {
      fetchAnalyses();
    }
    return () => {
      stopRefresh();
    };
  }, [projectId, fetchAnalyses, stopRefresh]);

  // Handle retry for failed analyses
  const handleRetryAnalysis = async (analysis, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    if (!analysis) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await retryAnalysis(analysis._id);
      // fetchAnalyses restarts polling automatically while the analysis is active
      await fetchAnalyses();
    } catch (error) {
      console.error('Error retrying analysis:', error);
      setError('Failed to retry analysis. Please try again later.');
      setLoading(false);
    }
  };

  // Handle retry from viewer (no event needed)
  const handleRetryFromViewer = (analysisId) => {
    if (!canAnalyze) return;
    const analysis = analyses.find(a => a._id === analysisId);
    if (analysis) {
      handleRetryAnalysis(analysis);
    }
  };

  // Handle delete click
  const handleDeleteClick = (analysis, event) => {
    event.stopPropagation();
    setAnalysisToDelete(analysis);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!analysisToDelete || !projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await deleteScreenplayAnalysis(analysisToDelete._id, projectId);
      
      // If the deleted analysis was selected, clear the selection
      if (selectedAnalysis && selectedAnalysis._id === analysisToDelete._id) {
        setSelectedAnalysis(null);
      }
      
      await fetchAnalyses();
      setDeleteDialogOpen(false);
      setAnalysisToDelete(null);
    } catch (err) {
      console.error('Error deleting analysis:', err);
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  };

  // Cancel delete
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAnalysisToDelete(null);
  };

  // Handle analysis selection
  const handleAnalysisClick = (analysis) => {
    setSelectedAnalysis(analysis);
  };

  // Handle back button click
  const handleBackClick = () => {
    setSelectedAnalysis(null);
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case 'completed':
        return { bg: theme.palette.success.main, text: theme.palette.success.contrastText };
      case 'processing':
        return { bg: theme.palette.info.main, text: theme.palette.info.contrastText };
      case 'pending':
        return { bg: theme.palette.warning.main, text: theme.palette.warning.contrastText };
      case 'error':
        return { bg: theme.palette.error.main, text: theme.palette.error.contrastText };
      case 'failed': // For backward compatibility
        return { bg: theme.palette.error.main, text: theme.palette.error.contrastText };
      default:
        return { bg: theme.palette.grey[500], text: theme.palette.getContrastText(theme.palette.grey[500]) };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // If a specific analysis is selected, show the viewer
  if (selectedAnalysis) {
    return (
      <ScreenplayAnalysisViewer 
        analysis={selectedAnalysis} 
        onBack={handleBackClick}
        projectId={projectId}
        projectName={projectName}
        retryAnalysis={handleRetryFromViewer}
      />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!canView && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You do not have permission to view screenplay analyses (requires scripts.view).
        </Alert>
      )}
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: theme.palette.background.paper,
        p: 2,
        borderRadius: 1,
        boxShadow: 1
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 500,
          color: theme.palette.text.primary 
        }}>
          Screenplay Analysis
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 1,
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          {error}
        </Alert>
      )}

      {!selectedScript && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            borderRadius: 1,
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          Please select a script to analyze
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : analyses.length === 0 ? (
        <Paper 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            borderRadius: 1
          }}
        >
          <AnalyticsIcon sx={{ fontSize: 60, color: theme.palette.text.secondary, mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No screenplay analyses yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Select a script and create a new analysis
          </Typography>
        </Paper>
      ) : (
        <Paper 
          sx={{ 
            flexGrow: 1, 
            overflow: 'auto',
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            borderRadius: 1
          }}
        >
          <List>
            {analyses.map((analysis, index) => (
              <React.Fragment key={analysis._id}>
                <ListItem 
                  button 
                  onClick={() => handleAnalysisClick(analysis)}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {analysis.status === 'completed' ? (
                          <ReportIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        ) : (
                          <AnalyticsIcon sx={{ mr: 1, color: theme.palette.grey[500] }} />
                        )}
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {(analysis.filmCoverageReport && analysis.filmCoverageReport.title) || 'Screenplay Analysis'}
                        </Typography>
                        <Chip
                          label={analysis.status.charAt(0).toUpperCase() + analysis.status.slice(1)}
                          size="small"
                          sx={{
                            ml: 2,
                            backgroundColor: getStatusChipColor(analysis.status).bg,
                            color: getStatusChipColor(analysis.status).text,
                            fontWeight: 500,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Box component="span" sx={{ display: 'block' }}>
                          Created: {formatDate(analysis.createdAt)}
                        </Box>
                        {['completed', 'completed_partial'].includes(analysis.status) && analysis.filmCoverageReport && (
                          <Box component="span" sx={{ display: 'flex', mt: 0.5 }}>
                            <Chip
                              label={`Rating: ${analysis.filmCoverageReport.ratingEstimate || 'N/A'}`}
                              size="small"
                              sx={{ mr: 1, fontSize: '0.7rem' }}
                            />
                            <Chip
                              label={`Marketability: ${analysis.filmCoverageReport.marketability || 'N/A'}`}
                              size="small"
                              sx={{ mr: 1, fontSize: '0.7rem' }}
                            />
                            <Chip
                              label={`Evaluation: ${analysis.filmCoverageReport.overallEvaluation || 'N/A'}`}
                              size="small"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                        )}
                        {['error', 'failed'].includes(analysis.status) && (
                          <Box component="span" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                            Error: {analysis.errorMessage || 'Unknown error'}
                          </Box>
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <ListItemSecondaryAction>
                    {['error', 'failed', 'completed_partial'].includes(analysis.status) && canAnalyze && (
                      <Tooltip title="Retry Analysis">
                        <IconButton 
                          edge="end" 
                          aria-label="retry" 
                          onClick={(e) => handleRetryAnalysis(analysis, e)}
                          sx={{ mr: 1 }}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {['completed', 'completed_partial'].includes(analysis.status) && (
                      <Tooltip title="View Analysis">
                        <IconButton 
                          edge="end" 
                          aria-label="view" 
                          onClick={() => handleAnalysisClick(analysis)}
                          sx={{ mr: 1 }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canAnalyze && (
                      <Tooltip title="Delete Analysis">
                        <IconButton 
                          edge="end" 
                          aria-label="delete" 
                          onClick={(e) => handleDeleteClick(analysis, e)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
                {index < analyses.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Screenplay Analysis</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this screenplay analysis? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScreenplayAnalysisManager;
